import { useCallback, useEffect, useMemo, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { ChevronsLeft, ChevronsRight, LogOut, Moon, Sun, Video } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import ButtonSidebar from 'components/ButtonSidebar';
import CockpitUserCard from 'components/CockpitUserCard/CockpitUserCard';
import { ToggleGroup, ToggleGroupItem } from 'components/ui/toggle-group';
import ConfirmLogoutModal from 'components/Logout/components/ConfirmLogoutModal';
import { Spin } from 'components/Spin';
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar';
import { ScrollArea } from 'components/ui/scroll-area';
import { Separator } from 'components/ui/separator';
import { Skeleton } from 'components/ui/skeleton';
import { useMenu } from 'global/MenuContext';
import { useMenuSelection } from 'global/MenuSelectionContext';
import { useUser } from 'global/UserContext';
import { useUserId, useUserName, useUserOrganization } from 'hooks/useCurrentUser';
import { useSideBar } from 'hooks/useSideBar';
import { useUserAvatar } from 'hooks/useUserAvatar';
import { cn } from 'lib/utils';
import usePingOnline from 'microfrontends/hooks/usePingOnline';
import { useOnboardingFlow } from 'pages/Cockpit/hooks/useOnboardingFlow';
import { useTheme } from 'providers/ThemeProvider';
import { MenuItem, isMeetingRoomFeature } from 'types/menuTypes';
import { formatUserName } from 'utils/formatUserName';
import { getMenuTabKey, resolveMeetingRoomExtras } from 'utils/menuExtras';
import { AvatarDefault, PoweredByAlgoricca, PoweredByAlgorriccaLight } from 'utils/utilsIcons';

const SideBar = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { logout } = useUser();
  const userId = useUserId();
  const userName = useUserName();
  const organization = useUserOrganization();
  const { open, setOpen, toggleSidebar } = useSideBar();
  const { menu, isLoading, error } = useMenu();
  const { avatarUrl, isLoading: loadingImage } = useUserAvatar(userId || '');
  const { isDark, toggleMode } = useTheme();
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [showUserCard, setShowUserCard] = useState(false);
  const { selectMenuByDocumentId, secondLevelMenu } = useMenuSelection();
  // State to control which first-level menus are expanded (cascade)
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());

  usePingOnline();

  // Close user card on route change (e.g. gear icon navigates away)
  const location = useLocation();
  useEffect(() => {
    setShowUserCard(false);
  }, [location.pathname]);

  // Get profile completion status
  const { completionStatus } = useOnboardingFlow(null);
  const profileCompletionPercentage = useMemo(
    () => completionStatus?.completionPercentage ?? 0,
    [completionStatus?.completionPercentage]
  );

  // Automatically expand the menu that contains the selected item
  useEffect(() => {
    if (secondLevelMenu) {
      // Find the first-level menu that contains the secondLevelMenu
      const parentMenu = menu.find(item =>
        item.subMenu?.some(sub => (sub.feature?.documentId || sub.path) === (secondLevelMenu.feature?.documentId || secondLevelMenu.path))
      );
      if (parentMenu) {
        setExpandedMenus(new Set([parentMenu.documentId || parentMenu.id || parentMenu.label]));
      }
    }
  }, [secondLevelMenu, menu]);

  // Function to toggle menu expansion
  const toggleMenuExpansion = useCallback((menuId: string) => {
    setExpandedMenus(prev => {
      if (prev.has(menuId)) {
        return new Set();
      }
      return new Set([menuId]);
    });
  }, []);

  // Handler for submenu item click (second level)
  const handleSubMenuItemClick = useCallback(
    (subMenuItem: MenuItem) => {
      const isMeetingRoom = subMenuItem.feature?.dynamic_component === 'MEETING_ROOM';
      const isMeetingRoomRoom = subMenuItem.feature?.dynamic_component === 'MEETING_ROOM_ROOM';

      // If it's a meeting room menu (MEETING_ROOM), navigate to the first submenu
      if (isMeetingRoom && subMenuItem.subMenu && subMenuItem.subMenu.length > 0) {
        const firstSubMenu = subMenuItem.subMenu[0];
        const extras = resolveMeetingRoomExtras(firstSubMenu);
        const documentId = extras?.internal_id || subMenuItem.documentId || '';
        selectMenuByDocumentId(documentId, true, undefined, firstSubMenu.path);
        navigate(firstSubMenu.path);
        return;
      }

      // If it's a specific room (MEETING_ROOM_ROOM)
      if (isMeetingRoomRoom) {
        const extras = resolveMeetingRoomExtras(subMenuItem);
        const documentId = extras?.internal_id || subMenuItem.documentId || '';
        selectMenuByDocumentId(documentId, true, undefined, subMenuItem.path);
        navigate(subMenuItem.path);
        return;
      }

      // If it's FORM_DYNAMIC with subMenu (form connections), navigate to the first subMenu
      const isFormDynamic = subMenuItem.feature?.dynamic_component === 'FORM_DYNAMIC';
      if (isFormDynamic && subMenuItem.subMenu && subMenuItem.subMenu.length > 0) {
        const firstSubMenu = subMenuItem.subMenu[0];
        const parentMenu = menu.find(item =>
          item.subMenu?.some(sub => (sub.feature?.documentId || sub.path) === (subMenuItem.feature?.documentId || subMenuItem.path))
        );
        if (parentMenu) {
          const tabKey = getMenuTabKey(firstSubMenu);
          selectMenuByDocumentId(
            tabKey,
            true,
            [parentMenu, subMenuItem, firstSubMenu],
            firstSubMenu.path
          );
          navigate(firstSubMenu.path);
          return;
        }
      }

      // For other menu types
      if (subMenuItem.feature?.documentId) {
        selectMenuByDocumentId(subMenuItem.feature.documentId, true, undefined, subMenuItem.path);
        navigate(subMenuItem.path);
        return;
      }

      // If no feature.documentId but has submenu, navigate to the first submenu
      if (
        subMenuItem.subMenu &&
        subMenuItem.subMenu.length > 0 &&
        subMenuItem.subMenu[0]?.feature?.documentId
      ) {
        const firstChild = subMenuItem.subMenu[0];
        selectMenuByDocumentId(
          firstChild.feature?.documentId || '',
          true,
          undefined,
          firstChild.path
        );
        navigate(firstChild.path);
      }
    },
    [navigate, selectMenuByDocumentId, menu]
  );

  const getInitials = useCallback((firstName?: string, lastName?: string) => {
    const first = firstName?.[0] || '';
    const last = lastName?.[0] || '';
    return `${first}${last}`.toUpperCase() || 'U';
  }, []);

  const handleOpenLogoutModal = () => setLogoutModalOpen(true);
  const handleCloseLogoutModal = () => setLogoutModalOpen(false);

  const handleConfirmLogout = useCallback(() => {
    setLogoutModalOpen(false);
    // Intentional 300ms delay: allows modal close animation to complete before logout unmounts the component
    setTimeout(() => {
      logout();
    }, 300);
  }, [logout]);

  const handleMenuClick = useCallback(
    (menuItem: MenuItem) => {
      // If the menu has submenu and no feature.documentId, toggle expansion (cascade)
      if (menuItem.subMenu?.length && !menuItem.feature?.documentId) {
        setOpen(true);
        toggleMenuExpansion(menuItem.documentId || menuItem.id || menuItem.label);
        return;
      }

      // If the menu has feature.documentId, navigate
      if (menuItem.feature?.documentId) {
        selectMenuByDocumentId(menuItem.feature.documentId, true, undefined, menuItem.path);
        navigate(menuItem.path || '#');
        return;
      }

      // If no submenu or feature, just navigate
      navigate(menuItem.path || '#');
    },
    [toggleMenuExpansion, selectMenuByDocumentId, navigate, setOpen]
  );

  if (isLoading) return <Spin />;

  if (error)
    return (
      <p className="text-small font-semibold text-destructive text-center">
        {t('sidebar.menuLoadError')}
      </p>
    );

  return (
    <>
      <nav
        role="navigation"
        aria-label={t('navigation.mainNavigation')}
        className={cn(
          'fixed min-h-screen h-screen flex flex-col z-[100]',
          'bg-background border-r border-border',
          'transition-all duration-200 ease-out',
          open ? 'w-60' : 'w-16'
        )}
      >
        {/* Collapse/Expand Toggle Button - Top Right */}
        <button
          type="button"
          onClick={toggleSidebar}
          className={cn(
            'absolute top-3 p-1.5 rounded-full',
            'bg-background border-2 border-primary',
            'ring-2 ring-primary/30',
            'hover:bg-muted transition-colors',
            'z-10',
            open ? 'right-3' : '-right-4'
          )}
          aria-label={open ? t('sidebar.collapseMenu') : t('sidebar.expandMenu')}
        >
          {open ? <ChevronsLeft className="h-4 w-4" /> : <ChevronsRight className="h-4 w-4" />}
        </button>

        {/* User Section */}
        <div
          className={cn('flex flex-col items-center p-3 flex-shrink-0', open ? 'gap-2' : 'gap-1')}
        >
          {/* Avatar with hover effect */}
          <div
            className={cn(
              'p-1 rounded-lg transition-colors',
              open && 'cursor-pointer hover:bg-muted/50 w-full'
            )}
            onClick={open ? () => setShowUserCard(true) : undefined}
          >
            <div className={cn('flex items-center', open ? 'gap-3' : 'justify-center')}>
              {loadingImage ? (
                <Skeleton className="rounded-full h-10 w-10" />
              ) : (
                <Avatar className="border-2 border-primary/20 transition-all h-10 w-10">
                  <AvatarImage src={avatarUrl || AvatarDefault} alt="Avatar" />
                  <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
                    {getInitials(userName?.firstName, userName?.lastName)}
                  </AvatarFallback>
                </Avatar>
              )}

              {open && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {formatUserName(userName?.fullName || '')}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {organization?.individual_plan?.name || t('sidebar.noPlan')}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Theme Toggle - only when expanded */}
          {open && (
            <div className="self-start pl-[56px] -mt-2">
              <ToggleGroup
                type="single"
                value={isDark ? 'dark' : 'light'}
                onValueChange={(value) => {
                  if (value && value !== (isDark ? 'dark' : 'light')) {
                    toggleMode();
                  }
                }}
                className="gap-0 h-6 bg-primary/5 border border-primary/10 backdrop-blur-sm"
              >
                <ToggleGroupItem value="light" aria-label={t('sidebar.lightMode')} className="flex-1 h-full px-2 data-[state=on]:bg-primary/15 data-[state=on]:text-primary data-[state=on]:shadow-none data-[state=on]:backdrop-blur-md data-[state=on]:border-primary/30">
                  <Sun className="h-3 w-3" />
                </ToggleGroupItem>
                <ToggleGroupItem value="dark" aria-label={t('sidebar.darkMode')} className="flex-1 h-full px-2 data-[state=on]:bg-primary/15 data-[state=on]:text-primary data-[state=on]:shadow-none data-[state=on]:backdrop-blur-md data-[state=on]:border-primary/30">
                  <Moon className="h-3 w-3" />
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          )}

          <Separator className={cn('my-1', open ? 'w-full' : 'w-8')} />
        </div>

        {/* Menu Items with ScrollArea */}
        <ScrollArea className="flex-1">
          <div className="py-2 px-2">
            <ul className="space-y-1" role="menubar">
              {menu.map(menuItem => {
                const hasSubMenu = menuItem.subMenu && menuItem.subMenu.length > 0;
                const isExpanded = expandedMenus.has(menuItem.documentId || menuItem.id || menuItem.label);
                const secondLevelMenus =
                  menuItem.subMenu?.filter(item => item.level === 'second') || [];

                return (
                  <li key={menuItem.documentId || menuItem.id || menuItem.label} role="none">
                    <ButtonSidebar
                      label={menuItem.label || ''}
                      path={menuItem.path || '#'}
                      icon={menuItem.icon}
                      isActive={menuItem.isActive}
                      subMenu={menuItem.subMenu || []}
                      disabled={!menuItem.isActive}
                      onClick={() => handleMenuClick(menuItem)}
                      level="first"
                      isExpanded={isExpanded}
                      feature={menuItem.feature}
                    />

                    {/* Cascade submenu (second level) */}
                    <AnimatePresence initial={false}>
                      {hasSubMenu && isExpanded && open && (
                        <motion.ul
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2, ease: 'easeInOut' }}
                          className="overflow-hidden"
                          role="menu"
                          aria-label={t('navigation.submenuOf', { label: menuItem.label })}
                        >
                          <div className="pl-4 py-1 space-y-0.5">
                            {secondLevelMenus.map((subItem, index) => (
                              <motion.li
                                key={subItem.feature?.documentId || subItem.path || index}
                                initial={{ x: -10, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: index * 0.03 }}
                                role="none"
                              >
                                <ButtonSidebar
                                  label={subItem.label}
                                  path={subItem.path}
                                  icon={subItem.icon || (isMeetingRoomFeature(subItem.feature) ? <Video className="w-6 h-6" /> : null)}
                                  isActive={subItem.isActive}
                                  disabled={!subItem.isActive}
                                  subMenu={subItem.subMenu || []}
                                  isSubMenu
                                  level="second"
                                  onClick={() => handleSubMenuItemClick(subItem)}
                                  feature={subItem.feature}
                                />
                              </motion.li>
                            ))}
                          </div>
                        </motion.ul>
                      )}
                    </AnimatePresence>
                  </li>
                );
              })}
            </ul>
          </div>
        </ScrollArea>

        {/* Separator above logout */}
        <Separator className="mx-3" />

        {/* Logout button - aligned left like other items */}
        <div className="px-3 py-2">
          <button
            type="button"
            onClick={handleOpenLogoutModal}
            className={cn(
              'flex items-center gap-3 px-3 py-2 rounded-md w-full',
              'text-sm font-medium text-foreground',
              'hover:bg-primary/10 hover:text-primary',
              'transition-colors duration-200',
              open ? 'justify-start' : 'justify-center'
            )}
            aria-label={t('sidebar.logoutPlatform')}
          >
            <LogOut className="h-4 w-4" />
            {open && <span>{t('sidebar.logout')}</span>}
          </button>
        </div>

        {/* Footer - Powered By (only when expanded) */}
        {open && (
          <div className="flex flex-col p-3 pb-0 flex-shrink-0 mt-auto border-t border-border">
            <div className="flex flex-col justify-center items-center gap-1">
              <span className="text-xs text-muted-foreground">{t('sidebar.poweredBy')}</span>
              <img
                src={isDark ? PoweredByAlgoricca : PoweredByAlgorriccaLight}
                alt="Powered by Algoricca"
                className="h-[90px] w-auto transition-all -mt-5"
              />
            </div>
          </div>
        )}

        <ConfirmLogoutModal
          open={logoutModalOpen}
          onClose={handleCloseLogoutModal}
          onConfirm={handleConfirmLogout}
        />
      </nav>

      {/* Dimmed overlay to close on outside click - OUTSIDE nav to avoid stacking context issues */}
      <AnimatePresence>
        {showUserCard && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/40 z-[110]"
            onClick={() => setShowUserCard(false)}
          />
        )}
      </AnimatePresence>

      {/* User Card Panel - slides right parallel to sidemenu - OUTSIDE nav */}
      <AnimatePresence>
        {showUserCard && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className={cn(
              'fixed top-0 z-[120]',
              'h-screen overflow-auto',
              'bg-background/95 backdrop-blur-sm',
              'border-r border-border shadow-xl',
              'w-80',
              open ? 'left-60' : 'left-16'
            )}
          >
            <div className="h-full w-full">
              <CockpitUserCard
                stats={{
                  profileCompletion: profileCompletionPercentage,
                }}
                onEditProfile={() => setShowUserCard(false)}
                onSettings={() => setShowUserCard(false)}
                onClose={() => setShowUserCard(false)}
                className="h-full"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SideBar;
